import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Group,
	Paper,
	Stack,
	Switch,
	Text,
	TextInput,
	Title,
} from '@mantine/core'
import { QRCodeCanvas } from 'qrcode.react'
import { Download, ExternalLink } from 'tabler-icons-react'
import { Link, useParams } from 'react-router-dom'
import {
	getExperience,
	updateExperience,
} from '../services/actions/experienceActions'
import { getOrgCommerceSettings } from '../services/actions/orgActions'

const ManageExperienceStorePage = () => {
	const { orgSlug, experienceSlug } = useParams()
	const qrRef = useRef(null)
	const [enabled, setEnabled] = useState(false)
	const [commerceSettings, setCommerceSettings] = useState(null)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState('')

	const storeUrl = useMemo(
		() =>
			`${window.location.origin}/${encodeURIComponent(orgSlug)}/experiences/${encodeURIComponent(experienceSlug)}/store`,
		[experienceSlug, orgSlug],
	)

	useEffect(() => {
		let cancelled = false
		const load = async () => {
			setLoading(true)
			setError('')
			try {
				const [experience, commerce] = await Promise.all([
					getExperience(experienceSlug, orgSlug),
					getOrgCommerceSettings(orgSlug),
				])
				if (cancelled) return
				setEnabled(Boolean(experience.experienceStoreEnabled))
				setCommerceSettings(commerce?.settings || null)
			} catch (loadError) {
				if (!cancelled) {
					setError(loadError.message || 'Failed to load store settings.')
				}
			} finally {
				if (!cancelled) setLoading(false)
			}
		}
		load()
		return () => {
			cancelled = true
		}
	}, [experienceSlug, orgSlug])

	const stripeReady = Boolean(
		commerceSettings?.stripeConnectedAccountId &&
			commerceSettings.stripeChargesEnabled &&
			commerceSettings.stripePayoutsEnabled,
	)

	const toggleStore = async (nextEnabled) => {
		setSaving(true)
		setError('')
		try {
			const updated = await updateExperience(experienceSlug, orgSlug, {
				experienceStoreEnabled: nextEnabled,
			})
			setEnabled(Boolean(updated.experienceStoreEnabled))
		} catch (saveError) {
			setError(saveError.message || 'Failed to update the experience store.')
		} finally {
			setSaving(false)
		}
	}

	const downloadQrCode = () => {
		const canvas = qrRef.current
		if (!canvas) return
		const link = document.createElement('a')
		link.download = `${experienceSlug}-experience-store-qr.png`
		link.href = canvas.toDataURL('image/png')
		link.click()
	}

	if (loading) {
		return (
			<Box px='md' py='md'>
				<Text>Loading store settings...</Text>
			</Box>
		)
	}

	return (
		<Box px='md' py='md' maw={760}>
			<Title order={2} mb='xs'>
				Experience Store
			</Title>
			<Text color='dimmed' mb='lg'>
				Let walk-up travelers purchase an item and receive a check-in pass.
			</Text>

			{error ? (
				<Alert color='red' title='Store settings not saved' mb='md'>
					{error}
				</Alert>
			) : null}

			{!stripeReady ? (
				<Alert color='yellow' title='Stripe setup required' mb='md'>
					<Stack spacing='sm'>
						<Text size='sm'>
							Complete Stripe commerce setup before enabling this store.
						</Text>
						<Button
							component={Link}
							to={`/${orgSlug}/admin/commerce`}
							variant='light'
							compact
							style={{ alignSelf: 'flex-start' }}
						>
							Open Commerce Settings
						</Button>
					</Stack>
				</Alert>
			) : null}

			<Paper withBorder p='lg' radius='md' mb='md'>
				<Group position='apart' align='center' noWrap>
					<Box>
						<Text weight={600}>Enable store</Text>
						<Text size='sm' color='dimmed'>
							Only item options marked “Show in Experience Store” appear.
						</Text>
					</Box>
					<Switch
						checked={enabled}
						onChange={(event) => toggleStore(event.currentTarget.checked)}
						disabled={saving || (!stripeReady && !enabled)}
						aria-label='Enable experience store'
					/>
				</Group>
			</Paper>

			<Paper withBorder p='lg' radius='md'>
				<Title order={4} mb='xs'>
					Store QR code
				</Title>
				<Text size='sm' color='dimmed' mb='md'>
					Print or display this QR code at check-in.
				</Text>
				<TextInput value={storeUrl} readOnly mb='md' aria-label='Store URL' />

				{enabled ? (
					<Stack align='flex-start' spacing='md'>
						<Box
							p='md'
							style={{
								background: '#fff',
								border: '1px solid #e9ecef',
								borderRadius: 8,
							}}
						>
							<QRCodeCanvas
								ref={qrRef}
								value={storeUrl}
								size={220}
								level='H'
								includeMargin
							/>
						</Box>
						<Group>
							<Button leftIcon={<Download size={16} />} onClick={downloadQrCode}>
								Download QR
							</Button>
							<Button
								component='a'
								href={storeUrl}
								target='_blank'
								rel='noreferrer'
								variant='default'
								leftIcon={<ExternalLink size={16} />}
							>
								Open Store
							</Button>
						</Group>
					</Stack>
				) : (
					<Text size='sm' color='dimmed'>
						Enable the store to download its QR code.
					</Text>
				)}
			</Paper>
		</Box>
	)
}

export default ManageExperienceStorePage
